import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { AlertTriangleIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Damerau-Levenshtein distance (small, in-file) — used to detect when the
 * user types a near-duplicate of an existing Firma / Yer entry, e.g.
 * "Anadolu Hstanesi" when "Anadolu Hastanesi" already exists in the tenant.
 * Returns the number of single-character edits (insert / delete / substitute /
 * adjacent-transposition) between two strings.
 */
const editDistance = (a, b) => {
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost,
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        dp[i][j] = Math.min(dp[i][j], dp[i - 2][j - 2] + 1);
      }
    }
  }
  return dp[m][n];
};

const norm = (s) => (s || '').toString().trim().toLocaleLowerCase('tr-TR');

/**
 * Returns the closest existing tenant supplier name to `candidate` IF the
 * names are similar enough to be suspected duplicates — but not exact.
 * Returns null when (a) the candidate is empty, (b) the candidate exactly
 * matches an existing entry, or (c) no entry is similar enough.
 *
 * Threshold: edit-distance ≤ 2, scaled to never trip on very short strings.
 */
const findNearMatch = (candidate, suppliers) => {
  const c = norm(candidate);
  if (!c || c.length < 4) return null;
  for (const s of suppliers) {
    if (norm(s.name) === c) return null;  // exact (case/space-insensitive)
  }
  let best = null;
  let bestDist = Infinity;
  for (const s of suppliers) {
    const d = editDistance(c, norm(s.name));
    if (d < bestDist) {
      bestDist = d;
      best = s;
    }
  }
  if (!best) return null;
  // Allow 1 edit for ≤8 chars, 2 edits for longer strings.
  const limit = c.length <= 8 ? 1 : 2;
  return bestDist <= limit ? best : null;
};

/**
 * SupplierAutocomplete — a thin combobox over the existing Input:
 *   • As the user types, we filter the tenant-scoped supplier list and show
 *     up to 8 suggestions in a small dropdown.
 *   • If the typed value is a near-duplicate of an existing entry (Damerau-
 *     Levenshtein distance ≤ 2), an inline warning offers to switch to the
 *     canonical name in one click.
 *
 * Props:
 *   value, onChange, placeholder, testId
 */
const SupplierAutocomplete = ({ value, onChange, placeholder, testId }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios
      .get(`${BACKEND_URL}/api/solo/suppliers`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setSuppliers(r.data?.suppliers || []))
      .catch(() => setSuppliers([]));
  }, []);

  // Close the suggestion dropdown when the user clicks outside the field.
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const filtered = useMemo(() => {
    const q = norm(value);
    if (!q) return suppliers.slice(0, 8);
    return suppliers
      .filter((s) => norm(s.name).includes(q))
      .slice(0, 8);
  }, [value, suppliers]);

  const nearMatch = useMemo(
    () => findNearMatch(value, suppliers),
    [value, suppliers],
  );

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        data-testid={testId}
        autoComplete="off"
      />

      {/* Suggestions dropdown */}
      {open && filtered.length > 0 && (
        <div
          className="absolute z-20 left-0 right-0 mt-1 max-h-60 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg"
          data-testid={`${testId}-suggestions`}
        >
          {filtered.map((s) => (
            <button
              key={s.name}
              type="button"
              onMouseDown={(e) => {
                // mousedown (not click) prevents the input blur from firing first
                e.preventDefault();
                onChange(s.name);
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-purple-50 text-sm flex items-center justify-between gap-2"
              data-testid={`${testId}-suggestion-item`}
            >
              <span className="truncate">{s.name}</span>
              <span className="text-xs text-gray-400 flex-shrink-0">
                {s.count}×
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Near-duplicate warning */}
      {nearMatch && !open && (
        <div
          className="mt-1.5 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5"
          data-testid={`${testId}-near-match`}
        >
          <AlertTriangleIcon className="w-3.5 h-3.5 flex-shrink-0 mt-px text-amber-600" />
          <div className="min-w-0">
            <span>
              <strong>{nearMatch.name}</strong>&apos;ı mı demek istediniz?
            </span>
            <button
              type="button"
              onClick={() => onChange(nearMatch.name)}
              className="ml-1 underline text-amber-900 hover:text-amber-700 font-medium"
              data-testid={`${testId}-use-near-match`}
            >
              evet, kullan
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierAutocomplete;
